import {
    DataLayerEventName,
    PagePosition,
    PlayerEvent,
    PlaylistType,
    Standard,
    VideoSourceMeta,
} from '@news-mono/web-common'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'
import { parseSourceFromMetadata } from '../../../content/Video/utils/get-metadata'
import { swmPlayerDebug } from '../providers/debug-log'

export interface Handler {
    key:
        | 'ready'
        | 'ads-request'
        | 'ads-load'
        | 'ima3-log'
        | 'ima3-ad-error'
        | 'ad-hard-timeout'
        | 'ima3error'
        | 'ima3-hard-timeout'
        | 'ads-ad-started'
        | 'ads-first-quartile'
        | 'ads-midpoint'
        | 'ads-third-quartile'
        | 'ads-ad-ended'
        | 'ads-allpods-completed'
        | 'ads-click'
        | 'ads-pause'
        | 'ads-play'
        | 'timeupdate'
        | 'play'
        | 'ended'
        | 'error'
        | 'fullscreenchange'
        | 'useractive'
        | 'userinactive'
        | 'volumechange'
        | 'ads-volumechange'
        | 'playing'
        | 'pause'

    action: (event?: any) => void
}

export const debugEvents = [
    'beforepluginsetup:$name',
    'abort',
    'beforepluginsetup',
    'canplay',
    'canplaythrough',
    'componentresize',
    'controlsdisabled',
    'controlsenabled',
    'dispose',
    'durationchange',
    'emptied',
    'ended',
    'enterFullWindow',
    'enterpictureinpicture',
    'error',
    'exitFullWindow',
    'firstplay',
    'fullscreenchange',
    'languagechange',
    'leavepictureinpicture',
    'loadeddata',
    'loadeddata',
    'loadedmetadata',
    'loadedmetadata',
    'loadstart',
    'pause',
    'play',
    'playbackrateschange',
    'playerresize',
    'playing',
    'pluginsetup',
    'posterchange',
    'progress',
    'ratechange',
    'ready',
    'resize',
    'seeked',
    'seeking',
    'sourceset',
    'stalled',
    'suspend',
    'tap',
    'textdata',
    'texttrackchange',
    'timeupdate',
    'timeupdate',
    'useractive',
    'userinactive',
    'usingcustomcontrols',
    'usingnativecontrols',
    'volumechange',
    'volumechange',
    'waiting',
    'pluginsetup:$name',
    'ima3-ready',
    'ima3error',
    'ima3-ad-error',
    'ads-request',
    'ads-load',
    'ads-ad-started',
    'ads-ad-ended',
    'ads-pause',
    'ads-play',
    'ads-ad-skipped',
    'ads-first-quartile',
    'ads-midpoint',
    'ads-third-quartile',
    'ads-click',
    'ads-volumechange',
    'ads-pod-started',
    'ads-pod-ended',
    'ads-allpods-completed',
    'ima3-ad-error',
    'ima3-ads-manager-loaded',
    'ima3-all-ads-completed',
    'ima3-click',
    'ima3-complete',
    'ima3-content-pause-requested',
    'ima3-first-quartile',
    'ima3-hardtimeout',
    'ima3-loaded',
    'ima3-log',
    'ima3-midpoint',
    'ima3-paused',
    'ima3-ready',
    'ima3-resumed',
    'ima3-started',
    'ima3-third-quartile',
    'ima3-volume-changed',
] as const

export type PlayerEvents = Handler['key']
export type DebugEvents = typeof debugEvents[number]

export interface PlayerState {
    ready: (handler: () => void) => void
    getOffsetWidth: () => number
    currentTime: () => number
    duration: () => number
    muted: () => boolean
    error: () => Videojs.VideoJsMediaError | null
    isFullscreen: () => boolean
    adMuted: () => boolean
    wasAutoplayed: () => boolean
    paused: () => boolean
    on: (
        event: PlayerEvents | DebugEvents,
        handler: (event?: any) => void,
    ) => void
    off: (event: PlayerEvents | DebugEvents, handler: () => void) => void
    currentVideo: () => VideoMetaDTO
    currentPlaylistType: () => PlaylistType
    currentPosition: () => number
    pagePosition: () => PagePosition
    variant: () => Standard['videoPlayerVariant']
    volume: () => number
}

const ssr = typeof document !== 'object'
export interface EventsHandlerProps {
    player: PlayerState
    onEvent: (event: PlayerEvent) => void
    adBlockEnabled: () => boolean
    hasAdUnitPath: boolean
}

export function partialPlayerEvent() {
    return {
        timeStamp: Date.now(),
        originator: 'player-event',
    }
}

/*
 * If an adblocker is installed the state will start at blocked and move to complete or no-ads (server side ads).
 * Otherwise the video will progress through the states from pre-request to complete (client side ads),
 * unless there are no ads to display - in that cases it will transition from loaded -> no-ads
 */
type AdState =
    | 'pre-request'
    | 'requested'
    | 'loaded'
    | 'started'
    | 'complete'
    | 'no-ads'
    | 'blocked'

/**
 * This class is responsible for listening to player events
 * then raising our video events
 */
export class EventsHandler {
    currentVideoId?: string
    /** Used to track raised milestone events */
    trackedMilestones: number[] = []
    adState?: AdState
    /** True when the video content is started (after emitting video.start event) */
    started = false
    adRequestStartTime?: number
    handlers: Handler[] = []
    adBlockEnabled?: () => boolean

    constructor(public props: EventsHandlerProps) {
        if (ssr) {
            return
        }

        this.handlers = [
            { key: 'ready', action: this.handleReady },
            { key: 'ads-request', action: this.handleAdRequest },
            { key: 'ads-load', action: this.handleAdLoad },
            {
                key: 'ima3-ad-error',
                action: this.handleAdError.bind(this, 'ima3-ad-error'),
            },
            {
                key: 'ima3error',
                action: this.handleAdError.bind(this, 'ima3error'),
            },
            {
                key: 'ad-hard-timeout',
                action: this.handleAdError.bind(this, 'ad-hard-timeout'),
            },
            {
                key: 'ima3-hard-timeout',
                action: this.handleAdError.bind(this, 'ima3-hard-timeout'),
            },
            { key: 'ads-ad-started', action: this.handleAdStart },
            { key: 'ads-first-quartile', action: this.handleAdFirstQuartile },
            { key: 'ads-midpoint', action: this.handleAdMidpoint },
            { key: 'ads-third-quartile', action: this.handleAdThirdQuartile },
            { key: 'ads-ad-ended', action: this.handleAdEnded },
            { key: 'ads-allpods-completed', action: this.handleAdPodsComplete },
            { key: 'ads-click', action: this.handleAdClick },
            { key: 'ads-pause', action: this.handleAdPause },
            { key: 'ads-play', action: this.handleAdPlay },
            { key: 'timeupdate', action: this.handleProgress },
            { key: 'play', action: this.handlePlay },
            { key: 'ended', action: this.handleComplete },
            { key: 'error', action: this.handleError },
            { key: 'fullscreenchange', action: this.handleFullscreenChange },
            { key: 'useractive', action: this.handleUserActive },
            { key: 'userinactive', action: this.handleUserInactive },
            { key: 'volumechange', action: this.handleVolumeChange },
            { key: 'ads-volumechange', action: this.handleAdVolumeChange },
            // Happens when the video starts playing regardless of whether an ad as played or there was no ad, or if an ad call explodes
            // https://docs.brightcove.com/brightcove-player/current-release/Player.html#event:playing
            // Currently only way to know if a video is starting reliably, works with vod/live, adfree & ad supported videos & videos with ad errors
            { key: 'playing', action: this.handleStart },
            { key: 'pause', action: this.handlePause },
            { key: 'ima3-log', action: this.handleImaLog },
        ]
        this.adBlockEnabled = props.adBlockEnabled
        this.adState = this.getInitialAdState()
        this.subscribe(this.handlers)
    }

    getInitialAdState(): AdState {
        if (!this.props.hasAdUnitPath) {
            return 'no-ads'
        }

        return this.adBlockEnabled && this.adBlockEnabled()
            ? 'blocked'
            : 'pre-request'
    }

    shouldTrackAdEvents() {
        return this.props.hasAdUnitPath
    }

    handleVolumeChange = () => {
        this.props.onEvent({
            type: DataLayerEventName.videoVolumeChange,
            ...partialPlayerEvent(),
            payload: {
                ...getStandardEventPayload(this.props.player),
                volume: this.props.player.volume(),
                muted: this.props.player.muted(),
            },
        })
    }

    handleAdVolumeChange = () => {
        if (!this.shouldTrackAdEvents()) {
            return
        }

        this.props.onEvent({
            type: DataLayerEventName.adVolumeChange,
            ...partialPlayerEvent(),
            payload: {
                ...getStandardEventPayload(this.props.player),
                volume: this.props.player.volume(),
                muted: this.props.player.adMuted(),
            },
        })
    }

    videoChanged() {
        // Reset for next video
        this.adState = this.getInitialAdState()
        this.started = false

        this.trackedMilestones = []

        this.props.onEvent({
            type: DataLayerEventName.videoAvailable,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handlePlay = () => {
        this.props.onEvent({
            type: DataLayerEventName.videoPlay,
            ...partialPlayerEvent(),
            payload: {
                ...getStandardEventPayload(this.props.player),
                autoplayed: this.props.player.wasAutoplayed(),
            },
        })
        this.currentVideoId = this.props.player.currentVideo().id
    }

    /*
     * video.start is fired when playing is fired by video js
     */
    handleStart = () => {
        // if the video has already started dont fire this again, as the playing event is raised by brightcove
        // more than once during playback for example when a user seeks forward or back in the video.
        // we just want the first occurance of the playing event
        if (this.started) {
            return
        }

        if (this.adState === 'pre-request') {
            // No ads were loaded, video must have no ads, so lets update this
            this.adState = 'no-ads'
        }

        this.started = true

        this.props.onEvent({
            type: DataLayerEventName.videoStart,
            ...partialPlayerEvent(),
            payload: {
                ...getStandardEventPayload(this.props.player),
                autoplayed: this.props.player.wasAutoplayed(),
            },
        })
    }

    handlePause = () => {
        this.props.onEvent({
            type: DataLayerEventName.videoPause,
            ...partialPlayerEvent(),
            payload: {
                ...getStandardEventPayload(this.props.player),
            },
        })
    }

    handleReady = () => {
        this.props.onEvent({
            type: DataLayerEventName.playerReady,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleProgress = () => {
        const currentTime = Math.round(this.props.player.currentTime())
        const videoLength = this.props.player.duration()

        const percentageComplete = Math.round((100 / videoLength) * currentTime)

        /*
         * Progress (timeupdate event) fires (inexplicably) after a video is 'ended' (ad state is reset),
         * but before a new video has been started or ads have been loaded :(
         */
        const inbetweenVideos =
            this.currentVideoId &&
            this.currentVideoId !== this.props.player.currentVideo().id

        if (this.adState === 'blocked' && !inbetweenVideos) {
            this.adState = 'no-ads'
        }

        this.raiseProcessMilestoneIfPassed(25, percentageComplete)
        this.raiseProcessMilestoneIfPassed(50, percentageComplete)
        this.raiseProcessMilestoneIfPassed(75, percentageComplete)
    }

    raiseProcessMilestoneIfPassed(
        milestonePercent: 25 | 50 | 75,
        percentageComplete: number,
    ) {
        if (percentageComplete < milestonePercent) {
            return
        }

        const hasBeenRaised =
            this.trackedMilestones.indexOf(milestonePercent) !== -1

        if (hasBeenRaised) {
            return
        }
        this.trackedMilestones.push(milestonePercent)
        this.props.onEvent({
            type: `video.milestone.${milestonePercent}` as any,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleComplete = () => {
        // this event is delayed for the first video when you have an ad blocker on
        this.props.onEvent({
            type: DataLayerEventName.videoEnd,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleError = () => {
        swmPlayerDebug('handlng player error')

        const videoPayloadData = toVideoEventMeta(
            this.props.player.currentVideo(),
            this.props.player.getOffsetWidth(),
        )

        const error = this.props.player.error()

        if (!error || error === null) {
            // if there is no error - we raise an error that this event was triggered without a error existing
            this.props.onEvent({
                type: DataLayerEventName.playerError,
                ...partialPlayerEvent(),
                payload: {
                    video: videoPayloadData,
                    error: {
                        message:
                            'Error event triggered but no error object exists',
                        code: 0,
                    },
                },
            })
            return
        }

        this.props.onEvent({
            type: DataLayerEventName.playerError,
            ...partialPlayerEvent(),
            payload: {
                video: videoPayloadData,
                error: {
                    message: error.message,
                    type: error.type,
                    code: error.code,
                },
            },
        })
    }

    handleAdRequest = () => {
        if (!this.shouldTrackAdEvents()) {
            return
        }

        this.adRequestStartTime = Date.now()
        this.adState = 'requested'

        this.props.onEvent({
            type: DataLayerEventName.adRequest,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleAdLoad = () => {
        if (!this.shouldTrackAdEvents()) {
            return
        }

        this.adState = 'loaded'
        const loadDuration = this.adRequestStartTime
            ? // We want to report in seconds, not ms
              (Date.now() - this.adRequestStartTime) / 1000
            : 0

        this.props.onEvent({
            type: DataLayerEventName.adLoad,
            ...partialPlayerEvent(),
            payload: {
                ...getStandardEventPayload(this.props.player),
                ad: {
                    loadDuration,
                },
            },
        })
    }

    handleAdError = (type: string) => {
        if (!this.shouldTrackAdEvents()) {
            return
        }

        swmPlayerDebug('handlng adError error')

        this.adState = 'complete'

        this.props.onEvent({
            type: DataLayerEventName.adError,
            ...partialPlayerEvent(),
            payload: {
                ...getStandardEventPayload(this.props.player),
                errorType: type,
            },
        })
    }

    handleAdStart = () => {
        if (!this.shouldTrackAdEvents()) {
            return
        }

        this.adState = 'started'
        this.props.onEvent({
            type: DataLayerEventName.adStart,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleAdFirstQuartile = () =>
        this.shouldTrackAdEvents() &&
        this.props.onEvent({
            type: DataLayerEventName.adMilestone25,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleAdMidpoint = () =>
        this.shouldTrackAdEvents() &&
        this.props.onEvent({
            type: DataLayerEventName.adMilestone50,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleAdThirdQuartile = () =>
        this.shouldTrackAdEvents() &&
        this.props.onEvent({
            type: DataLayerEventName.adMilestone75,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleAdEnded = () => {
        if (!this.shouldTrackAdEvents()) {
            return
        }

        this.adState = 'complete'
        this.props.onEvent({
            type: DataLayerEventName.adEnd,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleAdPodsComplete = () => {
        if (!this.shouldTrackAdEvents()) {
            return
        }

        this.adState = 'complete'
        this.props.onEvent({
            type: DataLayerEventName.adPodComplete,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleAdClick = () =>
        this.shouldTrackAdEvents() &&
        this.props.onEvent({
            type: DataLayerEventName.adClick,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleAdPause = () =>
        this.shouldTrackAdEvents() &&
        this.props.onEvent({
            type: DataLayerEventName.adPause,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleAdPlay = () =>
        this.shouldTrackAdEvents() &&
        this.props.onEvent({
            type: DataLayerEventName.adPlay,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleActive = () =>
        this.props.onEvent({
            type: DataLayerEventName.adPause,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleUserActive = () =>
        this.props.onEvent({
            type: DataLayerEventName.playerUserActive,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    // this has more of a delay than the current mouse off behaviour
    handleUserInactive = () =>
        this.props.onEvent({
            type: DataLayerEventName.playerUserInactive,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })

    handleFullscreenChange = () => {
        if (this.props.player.isFullscreen()) {
            return this.props.onEvent({
                type: DataLayerEventName.playerFullscreenEnter,
                ...partialPlayerEvent(),
                payload: getStandardEventPayload(this.props.player),
            })
        }
        return this.props.onEvent({
            type: DataLayerEventName.playerFullscreenExit,
            ...partialPlayerEvent(),
            payload: getStandardEventPayload(this.props.player),
        })
    }

    handleImaLog = (event: any) => {
        swmPlayerDebug('handlng imaLog error')

        // DPT-3972 Error Code: 1205 - The browser prevented playback initiated without user interaction.
        if (
            event?.originalEvent?.g?.adError?.g?.innerError?.errorCode === 1205
        ) {
            swmPlayerDebug('raising playToPause error')
            return this.props.onEvent({
                type: DataLayerEventName.playToPauseError,
                ...partialPlayerEvent(),
                payload: {
                    ...getStandardEventPayload(this.props.player),
                },
            })
        }
    }

    dispose = () => {
        this.unsubscribe(this.handlers)
    }

    private subscribe(handlers: Handler[]) {
        for (const handler of handlers) {
            const action = handler.action

            if (handler.key === 'ready') {
                // The ready event sometimes fires before we have subscribed to the events. This method ensures
                // that it fires, even if the event has already passed: https://docs.videojs.com/player#ready
                this.props.player.ready(action)
            } else {
                this.props.player.on(handler.key, action)
            }
        }

        if (swmPlayerDebug.enabled) {
            for (const event of debugEvents) {
                this.props.player.on(event, (payload) =>
                    swmPlayerDebug(event, payload),
                )
            }
        }
    }

    private unsubscribe(handlers: Handler[]) {
        for (const handler of handlers) {
            const action = handler.action

            this.props.player.off(handler.key, action)
        }

        for (const event of debugEvents) {
            this.props.player.off(event, () => swmPlayerDebug(event))
        }
    }
}

export function toVideoEventMeta(
    metadata: VideoMetaDTO,
    playerOffsetWidth: number,
): VideoSourceMeta {
    return {
        // This ensures a bunch of keys are available in the video metadata
        // even if not specified
        source: metadata.source || undefined,
        displaySource: parseSourceFromMetadata(
            metadata.source,
            metadata.sourceExtra,
        ),
        sourceExtra: metadata.sourceExtra || undefined,
        primaryTopic: metadata.primaryTopic,
        secondaryTopic: metadata.secondaryTopics.join(','),
        videoContentType: metadata.videoContentType || undefined,
        videoType: metadata.videoType || undefined,
        producer: metadata.producer[0] || undefined,
        byline: metadata.byline || undefined,

        id: metadata.id,
        name: metadata.name || undefined,
        // Do not want NaN as it crashes tests
        duration: metadata.duration ? metadata.duration / 1000 : 0,
        width: playerOffsetWidth,
        // height is calculated based on aspect ratio because the containers height is 0
        height: Math.floor(playerOffsetWidth / (16 / 9)),
        season: metadata.seriesInfo?.season,
        episode: metadata.seriesInfo?.episode,
        series: metadata.seriesInfo?.series,
        pubdate: metadata.publicationDate,
    }
}

export function getStandardEventPayload(playerState: PlayerState): Standard {
    return {
        playlistType: playerState.currentPlaylistType(),
        position: playerState.currentPosition(),
        muted: playerState.muted(),
        volume: playerState.volume(),
        pagePosition: playerState.pagePosition(),
        video: toVideoEventMeta(
            playerState.currentVideo(),
            playerState.getOffsetWidth(),
        ),
        videoPlayerVariant: playerState.variant(),
        paused: playerState.paused(),
    }
}
